import { useCallback, useState } from "react";
import { toast } from "sonner";
import { Card, CopyButton, ErrorBanner, Notice, PageHeader, Spinner, StatusBadge, fmt, useLoad } from "../../components/kit";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, errMsg } from "../../lib/api";
import { AbuseReports, RiskBadge, Screening } from "../../components/AdminAi";

const Row = ({ k, children, testId }) => (
  <div className="flex flex-col gap-1 border-t border-white/5 py-3 first:border-0 sm:flex-row sm:gap-6" data-testid={testId}>
    <span className="w-48 shrink-0 text-xs uppercase tracking-wider text-zinc-500">{k}</span><div className="text-sm text-zinc-200">{children}</div>
  </div>
);
const Yes = ({ ok, yes = "configured", no = "not configured" }) => <span className={ok ? "text-emerald-300" : "text-amber-300"}>{ok ? yes : no}</span>;

function eventsStatus(ev) {
  if (!ev.configured) return "paused";
  return ev.status.subscription_confirmed_at ? "active" : "pending";
}

function GatewayStatus({ gw }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const probe = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/smtp-gateway-check"); setRes(data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <span className={gw.running ? "text-emerald-300" : "text-red-300"}>{gw.running ? `running on port ${gw.port}` : `offline: ${gw.error || "unknown"}`}</span>
      <span className="ml-2 text-xs text-zinc-400">public host: {gw.public_host || "none"}</span>
      <Button size="sm" variant="outline" className="ml-3" onClick={probe} disabled={busy || !gw.running} data-testid="admin-smtp-gateway-check">{busy ? "Probing…" : "Probe gateway"}</Button>
      {res && <div className="mt-2"><Notice tone={res.ok ? "ok" : "error"} testId="admin-smtp-gateway-check-result">{res.detail}</Notice></div>}
      {gw.reason && <div className="mt-1 text-xs text-zinc-500">{gw.reason}</div>}
    </div>
  );
}

function Health() {
  const { data, error, loading, reload } = useLoad(() => api.get("/admin/health").then((r) => r.data));
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const runCheck = async () => {
    setBusy(true);
    try { const { data: d } = await api.post("/admin/smtp-check"); setCheck(d); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  return (
    <div className="space-y-6">
      <Card testId="admin-provider-status">
        <div className="mb-3 flex items-center justify-between"><span className="font-medium text-white">Provider status</span>
          <Button size="sm" onClick={runCheck} disabled={busy || !data.smtp.configured} data-testid="admin-smtp-check">{busy ? "Checking…" : "Test SMTP connection"}</Button></div>
        {check && <div className="mb-3"><Notice tone={check.ok ? "ok" : "error"} testId="admin-smtp-check-result">{check.detail}</Notice></div>}
        <Row k="SMTP relay"><Yes ok={data.smtp.configured} />{!data.smtp.configured && <span className="ml-2 text-xs text-zinc-500">missing: {data.smtp.missing.join(", ")}</span>}</Row>
        <Row k="Endpoint"><span className="font-mono text-xs">{data.smtp.host || "—"}:{data.smtp.port || "—"} · {data.smtp.tls_mode}</span></Row>
        <Row k="Credentials">username {data.smtp.username_set ? "set" : "missing"} · password {data.smtp.password_set ? "set" : "missing"} <span className="text-xs text-zinc-500">(values are never displayed)</span></Row>
        <Row k="AWS region">{data.aws_region || "—"}</Row>
        <Row k="SES API (domains)"><Yes ok={data.ses_api.configured} /> <div className="text-xs text-zinc-500">{data.ses_api.note}</div></Row>
        <Row k="SES sandbox" testId="admin-sandbox-status"><span className="text-amber-300">{data.sandbox_status}</span><div className="text-xs text-zinc-500">{data.sandbox_note}</div></Row>
        <Row k="Delivery events" testId="admin-events-status"><Yes ok={data.delivery_events.configured} /> <div className="text-xs text-zinc-500">{data.delivery_events.note}</div>
          <div className="mt-1 text-xs text-zinc-400">Subscription confirmed: {fmt(data.delivery_events.status.subscription_confirmed_at)} · Last event: {data.delivery_events.status.last_event_type || "—"} {data.delivery_events.status.last_event_at && `(${fmt(data.delivery_events.status.last_event_at)}, ${data.delivery_events.status.last_event_result})`}</div></Row>
        <Row k="Account emails" testId="admin-system-email-status"><Yes ok={data.system_email.ready} yes="sending live" no="held (logged for admins)" /> <span className="ml-2 font-mono text-xs text-zinc-400">{data.system_email.sender || "no sender set"}</span>
          {data.system_email.reason && <div className="text-xs text-amber-300">{data.system_email.reason}</div>}<div className="text-xs text-zinc-500">{data.system_email.note}</div></Row>
        <Row k="Queue">{data.queue.mode.replaceAll("_", " ")} · jobs {Object.entries(data.jobs).map(([k, v]) => `${k}: ${v}`).join(", ") || "none"}<div className="text-xs text-zinc-500">{data.queue.note}</div></Row>
        <Row k="SMTP gateway" testId="admin-smtp-gateway"><GatewayStatus gw={data.smtp_gateway} /></Row>
      </Card>
      <EventsSetup ev={data.delivery_events} region={data.aws_region} />
      <LiveTest status={data.system_email} />
      <Controls health={data} reload={reload} />
    </div>
  );
}

function EventsSetup({ ev, region }) {
  const steps = [
    `In Amazon SNS (${region || "your region"}), create a Standard topic, e.g. cario-ses-events. Copy its ARN.`,
    "Give the ARN to CARIO Mail (Emergent secret SES_EVENTS_SNS_TOPIC_ARN) and restart, so the endpoint below starts accepting that topic.",
    "In the SNS topic, create an HTTPS subscription to the endpoint below. CARIO Mail verifies the signature and confirms it automatically.",
    "In SES → Configuration sets, create a set (e.g. cario-events) with an event destination: Amazon SNS → your topic, events Delivery, Bounce, Complaint, Reject, Rendering failure, Delivery delay.",
    "In SES → Identities → oletko.online → Configuration set tab, assign it as the DEFAULT configuration set (this is what applies to mail sent by the Mail Manager \"Send to Internet\" rule). If your Mail Manager IAM role is scoped to resources, allow ses:SendRawEmail on that configuration set ARN.",
  ];
  return (
    <Card testId="admin-events-setup">
      <div className="mb-1 flex items-center gap-2 font-medium text-white">Delivery events setup <StatusBadge status={eventsStatus(ev)} /></div>
      <p className="mb-4 text-xs text-zinc-500">Until real SES events arrive, Delivered / Bounced / Complained stay “Unavailable”.</p>
      <ol className="mb-4 list-decimal space-y-2 pl-5 text-sm text-zinc-300">{steps.map((s) => <li key={s}>{s}</li>)}</ol>
      <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 p-3">
        <code className="flex-1 break-all font-mono text-xs text-purple-200" data-testid="admin-events-endpoint">{ev.endpoint}</code><CopyButton value={ev.endpoint} testId="admin-events-endpoint-copy" />
      </div>
      <div className="mt-3 text-xs text-zinc-500">Topic: <span className="font-mono text-zinc-300">{ev.topic_arn || "not set"}</span></div>
    </Card>
  );
}

const PENDING = ["queued", "retrying"];

function useLiveTest() {
  const [msg, setMsg] = useState(null);
  const poll = useCallback(async (id, n = 0) => {
    try {
      const { data } = await api.get(`/admin/live-test/${id}`);
      setMsg(data);
      if (PENDING.includes(data.status) && n < 30) setTimeout(() => poll(id, n + 1), 2000);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  const start = async (to) => {
    setMsg(null);
    const { data } = await api.post("/admin/live-test", { to });
    setMsg(data);
    poll(data.id);
  };
  return { msg, start };
}

async function sendPreview(to) {
  const { data } = await api.post("/admin/account-email-preview", { to });
  if (data.status === "accepted_by_smtp") toast.success(`Preview accepted by SMTP (${data.reason})`);
  else toast.error(data.reason);
}

function LiveTestResult({ msg }) {
  const done = !PENDING.includes(msg.status);
  return (
    <div className="mt-4 space-y-1 text-sm" data-testid="admin-live-test-result">
      <div className="flex items-center gap-2"><span className="font-mono text-xs text-zinc-400">{msg.id}</span><StatusBadge status={msg.status} /></div>
      {msg.provider && <div className="font-mono text-xs text-zinc-400">SMTP {msg.provider.code ?? "—"}: {msg.provider.response}</div>}
      {msg.error && <div className="text-xs text-red-300">{msg.error}</div>}
      {done && msg.status === "accepted" && <p className="text-xs text-zinc-500">Accepted by the SMTP server. Check the recipient's inbox (and spam) to confirm delivery.</p>}
    </div>
  );
}

function LiveTest({ status }) {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const { msg, start } = useLiveTest();
  const guarded = async (question, action) => {
    if (!window.confirm(question)) return;
    setBusy(true);
    try { await action(to); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const send = (e) => { e.preventDefault(); guarded(`Send a REAL email from ${status.sender} to ${to}?`, start); };
  const preview = () => guarded(`Send a REAL branded account-email preview to ${to}?`, sendPreview);
  return (
    <Card testId="admin-live-test">
      <div className="mb-1 font-medium text-white">Live delivery test</div>
      <p className="mb-4 text-xs text-zinc-500">Sends one real message from {status.sender || "SYSTEM_FROM_EMAIL"} through Amazon SES. In the SES sandbox the recipient must be a verified address.</p>
      {!status.ready && <div className="mb-4"><Notice tone="warn" title="Live test blocked" testId="admin-live-test-blocked">{status.reason}</Notice></div>}
      <form onSubmit={send} className="flex flex-col gap-2 sm:flex-row">
        <Input type="email" required placeholder="recipient@example.com" value={to} onChange={(e) => setTo(e.target.value)} disabled={!status.ready} aria-label="Recipient" data-testid="admin-live-test-to" />
        <Button type="submit" disabled={!status.ready || busy} data-testid="admin-live-test-send">{busy ? "Sending…" : "Send live test"}</Button>
        <Button type="button" variant="outline" disabled={!status.ready || busy || !to} className="border-white/10 bg-transparent hover:bg-white/5 hover:text-white" data-testid="admin-account-email-preview" onClick={preview}>Send account-email preview</Button>
      </form>
      {msg && <LiveTestResult msg={msg} />}
    </Card>
  );
}

function Controls({ health, reload }) {
  const [limits, setLimits] = useState(health.default_limits);
  const patch = async (body, ok) => { try { await api.patch("/admin/settings", body); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card testId="admin-controls">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div><div className="font-medium text-white">Global sending kill switch</div><p className="text-xs text-zinc-500">Immediately blocks new sends (503) and holds queued messages for every workspace.</p></div>
        <Switch checked={health.kill_switch} aria-label="Kill switch" data-testid="admin-kill-switch"
          onCheckedChange={(v) => (!v || window.confirm("Pause ALL sending platform-wide?")) && patch({ kill_switch: v }, v ? "Sending paused" : "Sending resumed")} />
      </div>
      {health.kill_switch && <Notice tone="error" title="Sending is paused platform-wide" testId="admin-kill-switch-on" />}
      <form className="mt-6 grid gap-3 sm:grid-cols-4" onSubmit={(e) => { e.preventDefault(); patch({ default_limits: { daily: +limits.daily, monthly: +limits.monthly, per_minute: +limits.per_minute } }, "Global default limits saved"); }}>
        {["daily", "monthly", "per_minute"].map((k) => (
          <div key={k} className="space-y-1.5"><Label htmlFor={`gl-${k}`}>{k.replace("_", " ")}</Label><Input id={`gl-${k}`} type="number" min={0} value={limits[k]} onChange={(e) => setLimits({ ...limits, [k]: e.target.value })} data-testid={`admin-limit-${k}`} /></div>
        ))}
        <div className="flex items-end"><Button type="submit" data-testid="admin-limits-save">Save defaults</Button></div>
      </form>
    </Card>
  );
}

function Workspaces() {
  const { data, error, loading, reload } = useLoad(() => api.get("/admin/workspaces").then((r) => r.data));
  const patch = async (id, body, ok) => { try { await api.patch(`/admin/workspaces/${id}`, body); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10"><table className="w-full min-w-[980px] text-left text-sm" data-testid="admin-workspaces-table">
      <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">Workspace</th><th className="p-3">Members</th><th className="p-3">Verified domains</th><th className="p-3">Live msgs 30d</th><th className="p-3">Failed 30d</th><th className="p-3">Flagged 30d</th><th className="p-3">Signup risk</th><th className="p-3">Limits d/m/min</th><th className="p-3">Suspended</th></tr></thead>
      <tbody>{data.map((w) => (
        <tr key={w.id} className="border-t border-white/5">
          <td className="p-3"><div className="text-zinc-200">{w.name}</div><div className="font-mono text-[10px] text-zinc-600">{w.id}</div></td>
          <td className="p-3">{w.members}</td><td className="p-3">{w.domains_verified}</td><td className="p-3">{w.messages_30d}</td><td className="p-3">{w.failed_30d}</td>
          <td className={`p-3 ${w.flagged_30d ? "text-amber-300" : ""}`}>{w.flagged_30d}</td><td className="p-3"><RiskBadge risk={w.risk} testId={`admin-ws-risk-${w.id}`} /></td>
          <td className="p-3"><button className="font-mono text-xs text-purple-300 underline-offset-2 hover:underline" data-testid={`admin-ws-limits-${w.id}`} onClick={() => {
            const v = window.prompt("Limits as daily,monthly,per_minute (blank = platform default)", `${w.limits.daily},${w.limits.monthly},${w.limits.per_minute}`);
            if (v === null) return;
            const [daily, monthly, per_minute] = v.split(",").map((x) => (x.trim() === "" ? null : Number(x)));
            patch(w.id, { limits: { daily, monthly, per_minute } }, "Limits updated");
          }}>{w.limits.unlimited ? "unlimited" : `${w.limits.daily}/${w.limits.monthly}/${w.limits.per_minute}`}</button></td>
          <td className="p-3"><Switch checked={w.suspended} aria-label={`Suspend ${w.name}`} data-testid={`admin-ws-suspend-${w.id}`} onCheckedChange={(v) => {
            const reason = v ? window.prompt("Reason for suspension") : null;
            if (v && !reason) return;
            patch(w.id, { suspended: v, suspended_reason: reason }, v ? "Workspace suspended" : "Workspace reinstated");
          }} />{w.suspended_reason && <div className="mt-1 text-[10px] text-red-300">{w.suspended_reason}</div>}
            <label className="mt-1 flex items-center gap-1 text-[10px] text-zinc-400"><Switch checked={!w.sandbox} aria-label={`Approve ${w.name}`} data-testid={`admin-ws-approve-${w.id}`}
              onCheckedChange={(v) => patch(w.id, { sandbox: !v }, v ? "Workspace approved (sandbox lifted)" : "Workspace back in sandbox")} />{w.sandbox ? "sandbox" : "approved"}</label></td>
        </tr>))}</tbody></table></div>
  );
}

function DomainsAdmin() {
  const { data, error, loading, reload } = useLoad(() => api.get("/admin/domains").then((r) => r.data));
  const attest = async (d) => {
    const note = window.prompt(`Confirm that AWS SES shows "${d.name}" as VERIFIED for sending in ${d.region || "the configured region"}. Add a note (e.g. who checked and when):`);
    if (!note) return;
    try { await api.post(`/admin/domains/${d.id}/attest`, { note }); toast.success("Domain marked verified (attested)"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const unverify = async (d) => { try { await api.post(`/admin/domains/${d.id}/unverify`); toast.success("Domain set to pending"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  return (
    <div className="space-y-4">
      <Notice tone="info" title="Manual ownership confirmation">Domains are normally verified automatically through the AWS SES API ("Check with AWS"). Use "Confirm verified in AWS" only for ownership reviews (domains that already existed in the platform AWS account) after confirming who owns them. Every confirmation is audit-logged.</Notice>
      <div className="overflow-x-auto rounded-xl border border-white/10"><table className="w-full min-w-[720px] text-left text-sm" data-testid="admin-domains-table">
        <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500"><tr><th className="p-3">Domain</th><th className="p-3">Workspace</th><th className="p-3">Status</th><th className="p-3">Risk</th><th className="p-3">Source</th><th /></tr></thead>
        <tbody>{data.map((d) => (
          <tr key={d.id} className="border-t border-white/5"><td className="p-3 font-mono text-xs text-zinc-200">{d.name}</td><td className="p-3 text-xs text-zinc-400">{d.workspace_name}</td>
            <td className="p-3"><StatusBadge status={d.status} /></td><td className="p-3"><RiskBadge risk={d.risk} testId={`admin-domain-risk-${d.name}`} /></td><td className="p-3 text-xs text-zinc-500">{d.verification_source?.replaceAll("_", " ") || "—"}{d.attestation_note && <div className="text-[10px]">“{d.attestation_note}”</div>}</td>
            <td className="p-3 text-right">{d.status === "verified" ? <Button size="sm" variant="ghost" className="text-xs hover:bg-white/5 hover:text-white" onClick={() => unverify(d)} data-testid={`admin-domain-unverify-${d.name}`}>Set pending</Button>
              : <Button size="sm" variant="outline" className="border-white/10 bg-transparent text-xs hover:bg-white/5 hover:text-white" onClick={() => attest(d)} data-testid={`admin-domain-attest-${d.name}`}>Confirm verified in AWS</Button>}</td></tr>
        ))}</tbody></table></div>
    </div>
  );
}

function SimpleList({ path, testId, render, empty }) {
  const { data, error, loading, reload } = useLoad(() => api.get(path).then((r) => r.data), [path]);
  if (loading) return <Spinner />;
  if (error) return <ErrorBanner error={error} />;
  if (!data.length) return <p className="text-sm text-zinc-500" data-testid={`${testId}-empty`}>{empty}</p>;
  return <div className="space-y-2" data-testid={testId}>{data.map((x) => render(x, reload))}</div>;
}

export default function Admin() {
  return (
    <div>
      <PageHeader eyebrow="Platform" title="Platform admin" description="Provider health, abuse controls and global configuration. Customer message content is only shown as a short excerpt for flagged emails." />
      <Tabs defaultValue="health">
        <TabsList className="mb-6 flex h-auto flex-wrap bg-white/[0.03]">
          {["health", "screening", "workspaces", "domains", "abuse", "audit", "account-emails"].map((t) => <TabsTrigger key={t} value={t} data-testid={`admin-tab-${t}`} className="capitalize">{t.replace("-", " ")}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="health"><Health /></TabsContent>
        <TabsContent value="screening"><Screening /></TabsContent>
        <TabsContent value="workspaces"><Workspaces /></TabsContent>
        <TabsContent value="domains"><DomainsAdmin /></TabsContent>
        <TabsContent value="abuse"><AbuseReports /></TabsContent>
        <TabsContent value="audit"><SimpleList path="/admin/audit" testId="admin-audit" empty="No events." render={(a) => (
          <div key={a.id} className="flex flex-wrap justify-between gap-2 border-b border-white/5 py-2 text-xs"><span className="font-mono text-purple-200">{a.action}</span><span className="text-zinc-400">{a.actor_email || a.actor_id || "system"}</span><span className="font-mono text-zinc-600">{a.workspace_id || "platform"}</span><span className="text-zinc-500">{fmt(a.created_at)}</span></div>)} /></TabsContent>
        <TabsContent value="account-emails">
          <div className="mb-4"><Notice tone="warn" title="Fallback log">Account emails (verification, password reset, invites) appear here with their links only when they could not be sent (e.g. the sender domain is not verified yet). Links are sensitive — share only with the account owner.</Notice></div>
          <SimpleList path="/admin/system-emails" testId="admin-system-emails" empty="No account emails." render={(m) => (
            <Card key={m.id}><div className="text-sm text-zinc-200">{m.kind.replace("_", " ")} → {m.to} <StatusBadge status={m.status === "not_sent" ? "held" : "accepted"} /></div>
              <div className="text-xs text-zinc-500">{m.reason} · {fmt(m.created_at)}</div>{m.link && <div className="mt-2 break-all font-mono text-[11px] text-purple-300" data-testid="system-email-link">{m.link}</div>}</Card>)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
